




















import { isEmpty } from 'lodash-es';
import { defineComponent, ref, computed, useContext, watch } from '@nuxtjs/composition-api';
import { PropType } from 'vue';
import { ProductBox, ProductWithPrices } from '~/utils/types/Product';
import { getIsBook } from '~/utils/solepro/products';
import { getFirstProLabel, getSecondProLabel, getLowestMetaPrice, getFirstBookLabel, getSecondBookLabel, getSecondPriceLabel } from '~/utils/helpers/product';
import axios from 'axios';
import SkeletonLoader from '~/components/SkeletonLoader/index.vue';

export default defineComponent({
  props: {
    product: {
      type: Object as PropType<ProductBox>,
    },
    pricesData: {
      type: Object as PropType<Record<string, any>>,
      default: () => { return {} }
    },
    pricetag: {
      type: String,
      default: 'h3'
    }
  },
  components: {
    SkeletonLoader,
  },
  setup(props) {
    const { product } = props;
    const { app: { i18n }, $fc } = useContext();

    const firstLabel = ref<string>('');
    const secondLabel = ref<string>('');
    const productWithPrices = ref<ProductWithPrices>({ ...product });
    const isLoading = ref(true);

    const getPriceLabels = async (priceData) => {
        try {
          productWithPrices.value = { ...productWithPrices.value, ...priceData };
          
          
          const productForLabels = !isEmpty(productWithPrices.value.additionalProducts)
          ? getLowestMetaPrice(productWithPrices.value.additionalProducts)
          : productWithPrices.value;
          
          const ivaSuffix = productForLabels.includes_iva ? '':' + IVA'

          const lowFrequencySpan = productForLabels?.paymentFrequencies?.[0];

          const varLength = !!productWithPrices.value.variants && productWithPrices.value.variants.length > 1;

          /* this component works for both book and non-book products */
          const isBookProduct = getIsBook(productForLabels);

          if (isBookProduct) {
            firstLabel.value = getFirstBookLabel(productForLabels, i18n);
            secondLabel.value = getSecondBookLabel(productForLabels, i18n, $fc, varLength);
          } else if (lowFrequencySpan) {
            firstLabel.value = getFirstProLabel(productForLabels, lowFrequencySpan, i18n, $fc, null, varLength)+ivaSuffix
            const tmpSecondLabel = getSecondProLabel(productForLabels, lowFrequencySpan, i18n, $fc, varLength)
            secondLabel.value = tmpSecondLabel && tmpSecondLabel.trim()!='' ? tmpSecondLabel+ivaSuffix : tmpSecondLabel
          } else {
            if (!productForLabels.price && productForLabels.variants?.length > 0) {
              firstLabel.value = `${i18n.tc('Starting from')} ${$fc(
                productForLabels.variants[0].product?.price_range?.minimum_price?.final_price?.value ?? 0
              )}`+ivaSuffix;
            } else {
              firstLabel.value = `${$fc( typeof productForLabels?.prices=='string'? productForLabels.prices : productForLabels.price)}`+ivaSuffix
              if(productForLabels && productForLabels.discountedPrices && (typeof productForLabels.discountedPrices == 'string' || typeof productForLabels?.discountedPrices=='number')){
                const tmpSecondLabel = getSecondPriceLabel(productForLabels,null,i18n,$fc)
                secondLabel.value = tmpSecondLabel && tmpSecondLabel.trim()!='' ? tmpSecondLabel+ivaSuffix : tmpSecondLabel
              }
            }
          }

        } catch (err) {
          console.error('Error loading product label prices:', err);
        } finally {
          isLoading.value = false;
        }
    }

    watch(() => props.pricesData, (pricesData) => {
        const priceData = pricesData?.[product.sku];
        if (priceData) {
          getPriceLabels(priceData);
        }
      },
      { immediate: true, deep: true }
    );

    const hasOnlyBuyable = computed(() =>
      productWithPrices.value.additionalProducts?.length > 0
        ? productWithPrices.value.additionalProducts.every((p: any) => p.buyable)
        : productWithPrices.value.buyable
    );

    const hasSomeBuyable = computed(() =>
      productWithPrices.value.additionalProducts?.length > 0 &&
      productWithPrices.value.additionalProducts.some((p: any) => p.buyable)
    );

    const isBookProduct = computed(() => getIsBook(productWithPrices.value));

    const showLabels = computed(() => {
      const hasBuyableCondition = (hasOnlyBuyable.value || hasSomeBuyable.value);
      const isBuyable = productWithPrices.value.buyable || !productWithPrices.value.agentFormLink;
      const baseCon = hasBuyableCondition && isBuyable;

      if (isBookProduct.value) {
        return baseCon && hasOnlyBuyable.value && !productWithPrices.value.externalBuyLink;
      }

      return baseCon;
    });

    return {
      firstLabel,
      secondLabel,
      hasOnlyBuyable,
      hasSomeBuyable,
      isLoading,
      showLabels
    };
  }

});
